import type { AttendanceStatusPresentation } from '../types/attendance';
import { formatMinutes } from './dateTime';

/**
 * Confirmed / already-used attendance status codes from the backend today
 * response and daily attendance status_code field. Unknown codes stay hidden.
 */
const STATUS: Record<string, Omit<AttendanceStatusPresentation, 'code'>> = {
  NOT_STARTED: {
    label: 'Not started',
    description: 'You have not checked in today.',
    category: 'neutral',
  },
  CHECKED_IN: {
    label: 'Checked in',
    description: 'Your workday is in progress.',
    category: 'info',
  },
  ON_BREAK: {
    label: 'On break',
    description: 'Your break is currently in progress.',
    category: 'warning',
  },
  CHECKED_OUT: {
    label: 'Day completed',
    description: 'Your workday is complete.',
    category: 'success',
  },
  PRESENT: {
    label: 'Present',
    description: 'Attendance was marked present for this day.',
    category: 'success',
  },
  ABSENT: {
    label: 'Absent',
    description: 'No attendance was recorded as present.',
    category: 'danger',
  },
  LOP: {
    label: 'LOP',
    description: 'This working day is not covered by approved leave.',
    category: 'danger',
  },
  HALF_DAY: {
    label: 'Half-Day Absent',
    description: 'This day is counted as a half-day absence.',
    category: 'danger',
  },
  HALF_DAY_LEAVE: {
    label: 'Half-Day Leave',
    description:
      'Half-day leave is recorded. The other half of the day can be worked.',
    category: 'info',
  },
  ON_LEAVE: {
    label: 'On leave',
    description: 'Leave is recorded for this day.',
    category: 'info',
  },
  WFH: {
    label: 'Working from home',
    description: 'Work from home is recorded for this day.',
    category: 'info',
  },
  HOLIDAY: {
    label: 'Holiday',
    description: 'This day is marked as an organization holiday.',
    category: 'neutral',
  },
  WEEKLY_OFF: {
    label: 'Weekly Off',
    description: 'This is a scheduled non-working day.',
    category: 'neutral',
  },
  MISSING_CHECKOUT: {
    label: 'Missing checkout',
    description: 'A check-in exists without a corresponding checkout.',
    category: 'warning',
  },
};

function normalizeStatusKey(status: string): string {
  return status.trim().toUpperCase().replace(/[\s-]+/g, '_');
}

export function getAttendanceStatusPresentation(
  status: string | null | undefined,
): AttendanceStatusPresentation {
  if (!status?.trim()) {
    return {
      code: null,
      label: 'Status unavailable',
      description: 'Attendance status is unavailable.',
      category: 'neutral',
    };
  }
  const normalized = normalizeStatusKey(status);
  const known = STATUS[normalized];
  if (!known) {
    return {
      code: status,
      label: 'Status unavailable',
      description: 'Attendance status is unavailable.',
      category: 'neutral',
    };
  }
  return { code: normalized, ...known };
}

export function getAttendanceStatusLabel(status: string | null | undefined) {
  return getAttendanceStatusPresentation(status).label;
}

/**
 * Today's status: the backend day status once the day is final (after
 * check-out), otherwise the live session state.
 */
export function getTodayAttendanceStatusLabel(
  attendance:
    | {
        currentStatus: string | null;
        dayStatus?: { label: string; isFinal: boolean } | null;
      }
    | null
    | undefined,
): string {
  if (attendance?.dayStatus?.isFinal) return attendance.dayStatus.label;
  return getAttendanceStatusLabel(attendance?.currentStatus);
}

export function getAttendanceStatusMessage(status: string | null | undefined) {
  return getAttendanceStatusPresentation(status).description;
}

export function getAttendanceStatusReason({
  status,
}: {
  status: string | null | undefined;
}): string | null {
  if (
    status?.trim().toUpperCase() === 'HALF_DAY_LEAVE'
  ) {
    return 'Half-day leave; the other half may be worked';
  }

  return null;
}

export const formatWorkedMinutes = formatMinutes;
